import React from "react";
import { Button } from "./ui/button";
import { ArrowRight, ShieldCheck, Stethoscope, FileCheck2 } from "lucide-react";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-12 md:pt-16 pb-16 md:pb-24 overflow-hidden"
    >
      {/* Background organic shapes */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full bg-brand-green-100 blur-3xl opacity-70" />
        <div className="absolute bottom-0 -left-24 w-[380px] h-[380px] rounded-full bg-brand-green-200 blur-3xl opacity-60" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-5 md:px-8 grid grid-cols-12 gap-y-8 md:gap-6 lg:gap-8 items-center">
        <div className="col-span-12 md:col-span-7">
          <span
            data-testid="hero-overline"
            className="inline-flex items-center gap-2 rounded-full bg-brand-green-100 text-[color:var(--brand-green-700)] px-4 py-1.5 text-xs font-bold tracking-[0.2em] uppercase"
          >
            <ShieldCheck className="w-4 h-4" /> US Healthcare Consulting
          </span>

          <h1
            data-testid="hero-title"
            className="font-display mt-6 text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.08] text-navy break-words"
          >
            Your one-stop partner for a thriving{" "}
            <span className="italic text-[color:var(--brand-green-700)]">US healthcare</span>{" "}
            practice.
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-6 text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed"
          >
            We help providers and facilities across all 50 states cut denials, accelerate
            collections, secure credentialing, and obtain medical licenses — backed by a core team
            with <strong className="text-navy">50+ years of combined US healthcare experience</strong>.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Button
              asChild
              data-testid="hero-cta-book"
              className="rounded-full bg-navy hover:bg-[color:var(--brand-navy-dark)] text-white px-7 py-6 text-base shadow-lg shadow-blue-900/10"
            >
              <a href="#consultation" className="inline-flex items-center gap-2">
                Book a Free Consultation <ArrowRight className="w-4 h-4" />
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              data-testid="hero-cta-services"
              className="rounded-full border-[color:var(--brand-green-300)] text-navy hover:bg-brand-green-100 px-7 py-6 text-base"
            >
              <a href="#rcm">Explore RCM Services</a>
            </Button>
          </div>

          <div className="mt-10 flex flex-wrap gap-6 text-sm text-slate-600">
            <div className="flex items-center gap-2" data-testid="hero-trust-1">
              <FileCheck2 className="w-4 h-4 text-[color:var(--brand-green-700)]" /> HIPAA-aligned
              workflows
            </div>
            <div className="flex items-center gap-2" data-testid="hero-trust-2">
              <ShieldCheck className="w-4 h-4 text-[color:var(--brand-green-700)]" /> SOC 2 posture
            </div>
            <div className="flex items-center gap-2" data-testid="hero-trust-3">
              <Stethoscope className="w-4 h-4 text-[color:var(--brand-green-700)]" /> Licensing in all
              50 states
            </div>
          </div>
        </div>

        <div className="col-span-12 md:col-span-5 relative">
          <div className="relative rounded-[1.5rem] md:rounded-[2rem] overflow-hidden shadow-2xl shadow-blue-900/10 border border-slate-200">
            <img
              src="https://images.unsplash.com/photo-1758691461888-b74515208d7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxkb2N0b3IlMjB1c2luZyUyMGNvbXB1dGVyJTIwb2ZmaWNlfGVufDB8fHx8MTc3Njk4NTM1M3ww&ixlib=rb-4.1.0&q=85"
              alt="Healthcare provider at work"
              className="w-full h-[280px] sm:h-[360px] md:h-[460px] object-cover"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a]/25 to-transparent" />
          </div>
          <div className="mt-4 md:mt-0 md:absolute md:-left-6 md:-bottom-6 bg-white rounded-2xl shadow-xl border border-slate-200 p-4 md:p-5 md:w-64 max-w-full">
            <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--brand-green-700)] font-bold">
              Precision
            </p>
            <p className="font-display text-xl md:text-2xl text-navy mt-1">That Performs.</p>
            <p className="text-xs text-slate-500 mt-2">
              Transparent pricing, compliant delivery, measurable ROI.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
